import React from 'react';
import PropTypes from 'prop-types';

import FpToaster from 'forepaas/toaster';
import FpTranslate from 'forepaas/translate';
import authService from '../../../services/auth-service';
import FpPasswordConfirm from './FpPasswordConfirm';
import { MaintenanceApi } from '../../../openapi/apis/MaintenanceApi';
import { createAuthenticatedApiClient } from '../../../helpers/api-client';
import { format } from 'date-fns';
import { ja as jaLocale } from 'date-fns/locale';

class FpPasswordForgot extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      form: {
        email: '',
      },
      step: 'request', // 'request' or 'confirm'
      isLoading: false,
      // Add maintenance state
      maintenanceData: null,
      isLoadingMaintenance: false,
    };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleConfirmSuccess = this.handleConfirmSuccess.bind(this);
  }

  componentDidMount() {
    // Fetch maintenance data when component mounts
    this.fetchMaintenanceData();
  }

  fetchMaintenanceData = async () => {
    this.setState({ isLoadingMaintenance: true });
    try {
      // Try with authenticated client first
      const maintenanceApi = createAuthenticatedApiClient(MaintenanceApi);
      const maintenanceData = await maintenanceApi.getScheduledMaintenance();
      this.setState({ 
        maintenanceData,
        isLoadingMaintenance: false 
      });
    } catch (error) {
      // If authentication fails, try without authentication (public endpoint)
      try {
        const { createConfiguration } = await import('../../../helpers/api-client');
        const maintenanceApi = new MaintenanceApi(createConfiguration());
        const maintenanceData = await maintenanceApi.getScheduledMaintenance();
        this.setState({ 
          maintenanceData,
          isLoadingMaintenance: false 
        });
      } catch (fallbackError) {
        console.error('Failed to fetch maintenance data:', fallbackError);
        this.setState({ 
          maintenanceData: null,
          isLoadingMaintenance: false 
        });
      }
    }
  };

  formatMaintenanceDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return format(date, 'yyyy年MM月dd日（EEE）HH:mm', { locale: jaLocale });
    } catch (error) {
      console.error('Failed to format date:', error);
      return dateString;
    }
  };

  renderMaintenanceNotice = () => {
    const { maintenanceData, isLoadingMaintenance } = this.state;

    if (isLoadingMaintenance) {
      return null;
    }

    // Don't show maintenance notice if no scheduled maintenance (id = 0)
    if (!maintenanceData || maintenanceData.id === 0) {
      return null;
    }

    const startTime = this.formatMaintenanceDate(maintenanceData.startTime);
    const endTime = this.formatMaintenanceDate(maintenanceData.endTime);

    return (
      <div className="maintenance-notice">
        <div className="notice-title">メンテナンスのお知らせ</div>
        <div className="notice-content">
          <p>下記日時にてメンテナンスを実施させていただきます。</p>
          <p>期間中はCloomをご利用いただけません。</p>
          <p>{startTime}～{endTime}</p>
          <p>※時間は前後する場合がございます。</p>
        </div>
      </div>
    );
  };

  handleInputChange(event) {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;
    var form = this.state.form;
    form[name] = value;
    this.setState({
      form,
    });
  }

  canSubmit() {
    return this.state.form.email.length > 0 && !this.state.isLoading;
  }

  handleSubmit(event) {
    event.preventDefault();
    if (this.canSubmit()) {
      this.setState({ isLoading: true });
      authService.resetPassword(this.state.form.email)
        .then(message => {
          this.setState({
            isLoading: false,
            step: 'confirm'
          });
          FpToaster.success('AuthenticationPasswordRenewSuccess');
        })
        .catch(err => {
          this.setState({ isLoading: false });
          
          // Check for blocked account errors
          const errorMessage = err.message || err.code || err;
          const isBlockedAccount = 
            errorMessage.includes('TooManyFailedAttemptsException') ||
            errorMessage.includes('TooManyRequestsException') ||
            errorMessage.includes('Attempt limit exceeded') ||
            errorMessage.includes('too many attempts') ||
            errorMessage.includes('blocked') ||
            errorMessage.includes('locked') ||
            (err.code && (err.code === 'TooManyFailedAttemptsException' || err.code === 'TooManyRequestsException'));
          
          if (isBlockedAccount) {
            FpToaster.error(FpTranslate('password_reset_blocked'));
          } else {
            FpToaster.error(err.message || FpTranslate('account_not_found'));
          }
        });
    }
  }

  handleConfirmSuccess() {
    // After successful password reset, close the dialog or redirect
    if (this.props.close) {
      this.props.close();
    }
  }

  renderRequestForm() {
    // Use brand prop to determine which logo to display
    // Requirements: 4_2
    const brand = this.props.brand || 'cloom';
    const logoSrc = brand === 'roomai' ? '/assets/img/roomai-logo.png' : '/assets/img/cloom-logo.png';
    const logoAlt = brand === 'roomai' ? 'RoomAI Logo' : 'Cloom Logo';

    return (
      <div className={`new-login-container ${brand === 'roomai' ? 'brand-roomai' : 'brand-cloom'}`}>
        {/* Left Panel - Logo and Maintenance Notice */}
        <div className="login-left-panel">
          <div className="logo-section">
            <div className="cloom-logo">
              <img src={logoSrc} alt={logoAlt} className="logo-image" />
            </div>
            {process.env.REACT_APP_ENV_TYPE !== 'prod' && (
              <div className="environment-text">{'開発環境'}</div>
            )}
          </div>

          {this.renderMaintenanceNotice()}

          <div className="version-info">Version: {process.env.REACT_APP_VERSION}</div>
        </div>

        {/* Right Panel - Forgot Password Form */}
        <div className="login-right-panel">
          <div className="login-form-container">
            <h1 className="login-title">ログイン</h1>
            
            <form className="login-form" onSubmit={this.handleSubmit}>
              <div className="form-group">
                <label className="form-label">
                  メールアドレス
                </label>
                <input
                  className="form-input"
                  type="email"
                  placeholder=""
                  value={this.state.form.email}
                  onChange={this.handleInputChange}
                  name="email"
                  style={{ color: '#FFFFFF' }}
                />
              </div>

              <button
                className="login-button"
                type="submit"
                disabled={!this.canSubmit()}
              >
                送信
              </button>

              <div className="forgot-password-container" style={{marginTop: 'unset'}}>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (this.props.close) this.props.close();
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (this.props.close) this.props.close();
                    }
                  }}
                  className="forgot-password-link"
                  style={{ display: 'inline' }}
                >
                  キャンセル
                </span>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  renderConfirmForm() {
    // Use brand prop to determine which logo to display
    // Requirements: 4_2
    const brand = this.props.brand || 'cloom';
    const logoSrc = brand === 'roomai' ? '/assets/img/roomai-logo.png' : '/assets/img/cloom-logo.png';
    const logoAlt = brand === 'roomai' ? 'RoomAI Logo' : 'Cloom Logo';

    return (
      <div className={`new-login-container ${brand === 'roomai' ? 'brand-roomai' : 'brand-cloom'}`}>
        {/* Left Panel - Logo and Maintenance Notice */}
        <div className="login-left-panel">
          <div className="logo-section">
            <div className="cloom-logo">
              <img src={logoSrc} alt={logoAlt} className="logo-image" />
            </div>
            {process.env.REACT_APP_ENV_TYPE !== 'prod' && (
              <div className="environment-text">{'開発環境'}</div>
            )}
          </div>

          {this.renderMaintenanceNotice()}

          <div className="version-info">Version: {process.env.REACT_APP_VERSION}</div>
        </div>

        {/* Right Panel - Password Confirm Form */}
        <div className="login-right-panel">
          <div className="login-form-container">
            <FpPasswordConfirm 
              close={this.props.close}
              color={this.props.color}
              email={this.state.form.email}
              onSuccess={this.handleConfirmSuccess}
            />
          </div>
        </div>
      </div>
    );
  }

  render() {
    return (
      <div className="forgot-password-form">
        {this.state.step === 'request' ? (
          this.renderRequestForm()
        ) : (
          this.renderConfirmForm()
        )}
      </div>
    );
  }
}

FpPasswordForgot.propTypes = {
  close: PropTypes.func,
  color: PropTypes.string,
  brand: PropTypes.string, // 'cloom' or 'roomai'
};

export default FpPasswordForgot;
